/**
 * Connectors OAuth return screen (/integrations/connect). The portal worker's /bff/oauth/connectors/callback (the
 * redirect URI registered with each provider) 303s here with `#state=&code=` or `#state=&error=`. This screen
 * removes the fragment from the address bar, POSTs state + code same-origin to /v1/connectors/oauth/callback
 * (the API checks the single-use tenant-bound state, sends the PKCE verifier and exchanges the code), lets the user
 * pick the accounts to connect (/v1/connectors/oauth/select), then returns to /integrations with the outcome.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Button, ErrorState, Heading, Loading, Text } from '@qm/ui';
import { useApp } from '../../../lib/app-context';
import { GroupConnectCallback } from './GroupConnectCallback';
import { YouTubeConnectCallback } from './YouTubeConnect';
import { BffError, bff, bffJson } from '../../../lib/bff';
import type { SetupView } from '@qm/contracts/customer-setup';
import { OAUTH_ERROR_CODE, OAUTH_ERROR_TOKEN } from '@qm/contracts/meta-oauth-error';

export interface CallbackParams {
  state: string | null; code: string | null; error: string | null;
  /** Meta's short error_reason / numeric error_code, when the provider sent them (never the free-text description) */
  errorReason?: string | null; errorCode?: string | null;
}

/** Parse the worker's fragment. Anything malformed is treated as an invalid callback. */
export function callbackFromHash(hash: string): CallbackParams {
  const p = new URLSearchParams(hash.replace(/^#/, ''));
  const state = p.get('state');
  const code = p.get('code');
  const error = p.get('error');
  const okState = state && /^[A-Za-z0-9_-]{1,64}$/.test(state) ? state : null;
  const okCode = code && code.length <= 2048 ? code : null;
  if (!okState) return { state: null, code: null, error: 'invalid_callback' };
  if (error || !okCode) {
    const okError = OAUTH_ERROR_TOKEN.test(error ?? '') ? error : 'invalid_callback';
    const reason = p.get('error_reason') ?? '';
    const errorCode = p.get('error_code') ?? '';
    return {
      state: okState, code: null, error: okError,
      errorReason: error && OAUTH_ERROR_TOKEN.test(reason) ? reason : null,
      errorCode: error && OAUTH_ERROR_CODE.test(errorCode) ? errorCode : null,
    };
  }
  return { state: okState, code: okCode, error: null };
}

/** i18n key for a failed connection (provider error in the fragment, or an API error code). */
export function connectErrorKey(code: string | null | undefined): string {
  switch (code) {
    case 'access_denied': case 'user_cancelled_login': case 'user_cancelled_authorize': case 'user_denied': return 'connectors.callback.denied';
    case 'oauth_state_expired': return 'connectors.connect.state_expired';
    case 'missing_scopes': return 'connectors.connect.missing_scopes';
    case 'property_unverified': return 'connectors.connect.property_unverified';
    case 'provider_not_configured': return 'connectors.callback.not_configured';
    default: return 'connectors.callback.failed';
  }
}

interface Account { ref: string; name: string | null; currency: string | null; isTest: boolean; verified: boolean }
interface CallbackResult { pendingId?: string; provider?: string; accounts?: Account[]; reconnected?: { provider: string } }

const apiErrorCode = (e: unknown) => (e instanceof BffError ? ((e.envelope?.error.details as { code?: string } | undefined)?.code ?? null) : null);
const back = (q: string) => {
 void bff<SetupView>('/v1/identity/setup').then(s => {
  window.location.assign(!s.completedAt && s.mode === 'guided' && s.step === 'accounts' ? '/setup' : `/integrations?${q}`);
 }, () => window.location.assign(`/integrations?${q}`));
};
/** Drive-import OAuth states (`dos_` ids) are completed by /v1/connectors/drive/oauth/callback. */
export const isDriveState = (state: string) => state.startsWith('dos_');

export function ConnectCallbackPage() {
  const [initial]=useState(()=>callbackFromHash(typeof window==='undefined'?'':window.location.hash));
  // YouTube channel states (`yts_`) come from /v1/social/youtube/oauth/start (social module, flag social.youtube)
  if (initial.state?.startsWith('yts_')) return <YouTubeConnectCallback params={initial} />;
  return initial.state?.startsWith('gcs_')?<GroupConnectCallback params={initial}/>:<LegacyConnectCallbackPage/>;
}
function LegacyConnectCallbackPage() {
  const { t } = useApp();
  const [phase, setPhase] = useState<'working' | 'choose' | 'error'>('working');
  const [errorKey, setErrorKey] = useState('connectors.callback.failed');
  const [pending, setPending] = useState<{ id: string; provider: string; accounts: Account[] } | null>(null);
  const [chosen, setChosen] = useState<Set<string>>(new Set());
  const [backTo, setBackTo] = useState('/integrations');
  const started = useRef(false);

  const fail = (code: string | null | undefined) => { setErrorKey(connectErrorKey(code)); setPhase('error'); };

  useEffect(() => {
    if (typeof window === 'undefined' || started.current) return;
    started.current = true;
    const p = callbackFromHash(window.location.hash);
    // the code is single-use and short-lived, but it must not stay in history or be re-posted on reload
    history.replaceState(null, '', window.location.pathname);
    // cloud drive import (Google Drive / OneDrive) states come from /v1/connectors/drive/oauth/start
    if (p.state && isDriveState(p.state)) setBackTo('/content/assets');
    if (p.error || !p.state || !p.code) { fail(p.error); return; }
    if (isDriveState(p.state)) {
      bffJson<{ provider: string }>('/v1/connectors/drive/oauth/callback', 'POST', { state: p.state, code: p.code }).then(
        (r) => window.location.assign(`/content/assets?drive=${encodeURIComponent(r.provider)}`),
        (e: unknown) => fail(apiErrorCode(e)),
      );
      return;
    }
    bffJson<CallbackResult>('/v1/connectors/oauth/callback', 'POST', { state: p.state, code: p.code }).then((r) => {
      if (r.reconnected) { back(`connected=${encodeURIComponent(r.reconnected.provider)}&reconnected=1`); return; }
      if (!r.pendingId || !r.provider) { fail(null); return; }
      const accounts = r.accounts ?? [];
      setPending({ id: r.pendingId, provider: r.provider, accounts });
      setChosen(new Set(accounts.length === 1 ? [accounts[0]!.ref] : []));
      setPhase('choose');
    }, (e: unknown) => fail(apiErrorCode(e)));
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!pending || !chosen.size) return;
    try {
      await bffJson('/v1/connectors/oauth/select', 'POST', { pendingId: pending.id, accountRefs: [...chosen] });
      back(`connected=${encodeURIComponent(pending.provider)}`);
    } catch (err) { fail(apiErrorCode(err)); }
  };
  const toggle = (ref: string, on: boolean) => setChosen((s) => { const n = new Set(s); if (on) n.add(ref); else n.delete(ref); return n; });

  return (
    <section className="qm-page" aria-labelledby="page-title">
      <a href="/setup">{t('shell.nav.setup')}</a>
      <div className="qm-page__head">
        <div>
          <Heading level={1} id="page-title">{t('connectors.callback.title')}</Heading>
          {pending ? <Text tone="muted">{t(`connectors.provider.${pending.provider}`)}</Text> : null}
        </div>
      </div>
      {phase === 'working' ? <Loading label={t('connectors.callback.working')} /> : null}
      {phase === 'error' ? (
        <div role="alert">
          <ErrorState message={t(errorKey)} />
          <Button onClick={() => window.location.assign(backTo)}>{t('connectors.callback.back')}</Button>
        </div>
      ) : null}
      {phase === 'choose' && pending ? (
        pending.accounts.length === 0 ? (
          <div role="status">
            <Text>{t('connectors.callback.no_accounts')}</Text>
            <Button onClick={() => window.location.assign('/integrations')}>{t('connectors.callback.back')}</Button>
          </div>
        ) : (
          <form onSubmit={(e) => void submit(e)}>
            <fieldset>
              <legend>{t('connectors.connect.choose_accounts')}</legend>
              {pending.accounts.map((a, i) => (
                <div key={a.ref}>
                  <input type="checkbox" id={`acct-${i}`} checked={chosen.has(a.ref)} onChange={(ev) => toggle(a.ref, ev.target.checked)} />
                  <label htmlFor={`acct-${i}`}><bdi>{a.name ?? a.ref}</bdi>{a.currency ? ` (${a.currency})` : ''}</label>
                </div>
              ))}
            </fieldset>
            <Text tone="muted">{t('connectors.connect.scopes_note')}</Text>
            <Button type="submit" variant="primary" disabled={!chosen.size}>{t('connectors.callback.save')}</Button>
          </form>
        )
      ) : null}
    </section>
  );
}
